import type { RawRows, NameColumnConfig } from './types';
import { getEmployeeName } from './findNameColumns';
import { normalizeEmployeeName } from './normalizeEmployeeName';
import { addAmounts, parseNumber } from './parseNumber';

// Within-file aggregator. NOT a Universal port — Smart Import's own bug fix
// (Functional Spec §8). Groups rows by employee, sums numeric columns, and emits
// one row per employee. Fixes the Lovable prototype's row-overwrite bug for
// providers (e.g. Paylocity) that export one row per paycheck.

function isNumericColumn(samples: string[]): boolean {
	const nonEmpty = samples
		.map((v) => String(v ?? '').trim())
		.filter((v) => v !== '');
	if (nonEmpty.length === 0) return false;
	const numeric = nonEmpty.filter((v) => Number.isFinite(parseNumber(v))).length;
	return numeric / nonEmpty.length >= 0.5;
}

// Groups dataRows' INDICES (not the rows themselves) by employee, so both
// aggregateByEmployee and confidenceByEmployee (P2.2) can share the exact same
// grouping decision while each reduces a group differently (summing cell values
// vs. worst-of confidence).
//
// ENG-369: blank-name rows all normalize to '' and would otherwise collapse into
// one group with their costs summed — masking a data problem as a single fake
// employee. Give each blank row its own unique key so it survives as a distinct
// row into Step 3, where computeEmployeeRows flags it 'blank_name' for the user
// to resolve individually. The key is internal to this function (the output
// matrix rebuilds cells and buildAggregatedRows re-derives csvName), so the
// leading-space sentinel can't collide with any real name — normalizeEmployeeName
// trims and title-cases, so no real key starts with a space.
function groupRowIndicesByEmployee(
	dataRows: RawRows,
	nameColConfig: NonNullable<NameColumnConfig>
): { order: string[]; groups: Map<string, number[]> } {
	const order: string[] = [];
	const groups = new Map<string, number[]>();
	let blankSeq = 0;
	dataRows.forEach((row, i) => {
		const normalized = normalizeEmployeeName(getEmployeeName(row, nameColConfig));
		const key = normalized === '' ? ` blank ${blankSeq++}` : normalized;
		if (!groups.has(key)) {
			groups.set(key, []);
			order.push(key);
		}
		groups.get(key)!.push(i);
	});
	return { order, groups };
}

/**
 * @param headers   the detected header row (drives column count)
 * @param dataRows  data rows ONLY (header row already removed; total rows already filtered)
 * @param nameColConfig the detected name-column layout
 * @returns one row per unique (normalized) employee name, numeric columns summed,
 *          non-numeric columns taking the first non-empty value. Output order
 *          follows first appearance.
 */
export function aggregateByEmployee(
	headers: string[],
	dataRows: RawRows,
	nameColConfig: NonNullable<NameColumnConfig>
): RawRows {
	const colCount = Math.max(
		headers.length,
		...dataRows.map((r) => r.length),
		0
	);

	// Decide which columns are numeric (summable) by scanning the FULL column, not
	// a leading sample. Sparse columns — voluntary benefits, loan repayments, etc. —
	// are often blank for the first many rows and only populated for employees lower
	// in the file. A leading-window sample (formerly the first 25 rows) saw no values
	// and misclassified those columns as text, so the aggregator took the first row's
	// value instead of summing across rows for multi-row employees (ENG-376).
	const numericCol: boolean[] = [];
	for (let c = 0; c < colCount; c++) {
		const samples = dataRows.map((r) => r[c] ?? '');
		numericCol[c] = isNumericColumn(samples);
	}

	const { order, groups } = groupRowIndicesByEmployee(dataRows, nameColConfig);

	const result: RawRows = [];
	for (const key of order) {
		const rows = groups.get(key)!.map((i) => dataRows[i]);
		const out: string[] = [];
		for (let c = 0; c < colCount; c++) {
			if (numericCol[c]) {
				// A cell that isn't a readable amount is left out of the sum rather
				// than poisoning it: acc + NaN used to turn the employee's whole
				// column into NaN, which Step 3 then imported as 0.
				const sum = rows.reduce((acc, r) => {
					const n = parseNumber(r[c]);
					return Number.isFinite(n) ? addAmounts(acc, n) : acc;
				}, 0);
				out[c] = String(sum);
			} else {
				const firstNonEmpty =
					rows.map((r) => String(r[c] ?? '')).find((v) => v.trim() !== '') ?? '';
				out[c] = firstNonEmpty;
			}
		}
		result.push(out);
	}

	return result;
}

/**
 * P2.2 — per-employee AI extraction confidence, aligned 1:1 (same order) with
 * aggregateByEmployee's output for the SAME (dataRows, nameColConfig). Only
 * meaningful for PDF-sourced imports (see PdfExtractionMetadata.rowConfidence);
 * CSV/XLSX rows carry no confidence, so this is never called for them.
 *
 * A group's confidence is 'low' if ANY of its contributing source rows was
 * 'low' — an employee aggregated from several line items shouldn't read as
 * fully trustworthy just because most of its rows were confident.
 *
 * @param dataRows      the SAME array (same order) passed to aggregateByEmployee
 * @param rowConfidence per-source-row confidence, same order/length as dataRows
 */
export function confidenceByEmployee(
	dataRows: RawRows,
	nameColConfig: NonNullable<NameColumnConfig>,
	rowConfidence: Array<'high' | 'low'>
): Array<'high' | 'low'> {
	const { order, groups } = groupRowIndicesByEmployee(dataRows, nameColConfig);
	return order.map((key) =>
		groups.get(key)!.some((i) => rowConfidence[i] === 'low') ? 'low' : 'high'
	);
}
